import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-full bg-sage-600 flex items-center justify-center text-white font-bold">
                SM
              </div>
              <div>
                <div className="font-semibold text-stone-900">Samuel Magalhães</div>
                <div className="text-xs text-stone-600">Medicina Tradicional Chinesa</div>
              </div>
            </div>
            <Button onClick={() => navigate('/')} variant="outline" className="border-sage-600 text-sage-700">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao Site
            </Button>
          </div>
        </nav>
      </header>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">Política de Privacidade</h1>
            <p className="text-stone-600 mb-8">Última atualização: Janeiro de 2025</p>

            <div className="space-y-8">
              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>1. Introdução</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Samuel Magalhães, terapeuta de Medicina Tradicional Chinesa, respeita a sua privacidade e está comprometido em proteger os seus dados pessoais. Esta política de privacidade informa sobre como tratamos os seus dados pessoais quando visita o nosso website e esclarece os seus direitos de privacidade.
                  </p>
                  <p>
                    Este website cumpre o Regulamento Geral sobre a Proteção de Dados (RGPD - Regulamento UE 2016/679) e a Lei n.º 58/2019, de 8 de agosto, que assegura a execução do RGPD em Portugal.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>2. Responsável pelo Tratamento de Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p><strong>Nome:</strong> Samuel Magalhães</p>
                  <p><strong>Atividade:</strong> Terapeuta de Medicina Tradicional Chinesa</p>
                  <p><strong>Email:</strong> samuelmagalhaes.mtc@gmail.com</p>
                  <p><strong>Telefone:</strong> 961 509 484</p>
                  <p><strong>Locais de atendimento:</strong> Porto e Braga, Portugal</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>3. Dados Pessoais que Recolhemos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>Podemos recolher, usar, armazenar e transferir diferentes tipos de dados pessoais sobre si:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Dados de Identidade:</strong> nome completo</li>
                    <li><strong>Dados de Contacto:</strong> endereço de email, número de telefone</li>
                    <li><strong>Dados de Saúde:</strong> informações sobre dores, ansiedade e sintomas (apenas quando fornecidos voluntariamente através do formulário de avaliação)</li>
                    <li><strong>Dados de Comunicação:</strong> mensagens e observações que nos envia através dos formulários</li>
                    <li><strong>Dados Técnicos:</strong> endereço IP, tipo de navegador, fuso horário, sistema operativo</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>4. Como Recolhemos os Seus Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>Recolhemos dados pessoais através de:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Formulário de Marcação de Consultas:</strong> quando solicita uma marcação</li>
                    <li><strong>Formulário de Subscrição:</strong> quando se inscreve para receber informações e dicas</li>
                    <li><strong>Formulário de Avaliação:</strong> quando preenche o questionário sobre dores e ansiedade</li>
                    <li><strong>Contacto direto:</strong> quando nos contacta por email ou telefone</li>
                    <li><strong>Tecnologias automatizadas:</strong> dados técnicos sobre o seu dispositivo e navegação</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>5. Como Utilizamos os Seus Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>Utilizamos os seus dados pessoais para as seguintes finalidades:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Marcação de consultas:</strong> processar e confirmar os seus pedidos de marcação</li>
                    <li><strong>Comunicação:</strong> responder às suas questões e pedidos de informação</li>
                    <li><strong>Prestação de serviços:</strong> fornecer tratamentos de Medicina Tradicional Chinesa</li>
                    <li><strong>Newsletter:</strong> enviar informações, dicas e novidades sobre terapias naturais (apenas se subscrever)</li>
                    <li><strong>Avaliação clínica:</strong> analisar a sua situação de saúde e recomendar tratamentos adequados</li>
                    <li><strong>Melhoria do website:</strong> melhorar a funcionalidade e experiência do utilizador</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>6. Base Legal para o Tratamento</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>Tratamos os seus dados pessoais com base em:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Consentimento:</strong> quando nos fornece voluntariamente os seus dados através dos formulários</li>
                    <li><strong>Execução de contrato:</strong> para prestar os serviços terapêuticos solicitados</li>
                    <li><strong>Interesses legítimos:</strong> para melhorar os nossos serviços e comunicar eficazmente</li>
                    <li><strong>Obrigações legais:</strong> para cumprir requisitos legais e regulamentares</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>7. Partilha de Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Os seus dados pessoais são confidenciais e não serão vendidos, alugados ou partilhados com terceiros para fins comerciais. Podemos partilhar dados apenas:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Com prestadores de serviços técnicos (hosting, email) que nos ajudam a operar o website</li>
                    <li>Se exigido por lei ou por autoridades competentes</li>
                    <li>Com o seu consentimento explícito</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>8. Segurança dos Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais contra acesso não autorizado, alteração, divulgação ou destruição. Estas medidas incluem:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Encriptação de dados sensíveis</li>
                    <li>Acesso restrito aos dados pessoais</li>
                    <li>Proteção dos sistemas informáticos</li>
                    <li>Backups regulares dos dados</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>9. Retenção de Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Conservamos os seus dados pessoais apenas pelo tempo necessário para cumprir as finalidades para as quais foram recolhidos:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Dados de marcação:</strong> até 2 anos após a última consulta</li>
                    <li><strong>Dados de saúde:</strong> conforme obrigações legais (mínimo 5 anos)</li>
                    <li><strong>Newsletter:</strong> até cancelar a subscrição</li>
                    <li><strong>Dados técnicos:</strong> até 12 meses</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>10. Os Seus Direitos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>De acordo com o RGPD, tem os seguintes direitos:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Direito de acesso:</strong> obter informação sobre os dados que tratamos</li>
                    <li><strong>Direito de retificação:</strong> corrigir dados incorretos ou incompletos</li>
                    <li><strong>Direito ao apagamento:</strong> solicitar a eliminação dos seus dados</li>
                    <li><strong>Direito à limitação:</strong> restringir o tratamento dos seus dados</li>
                    <li><strong>Direito à portabilidade:</strong> receber os seus dados em formato estruturado</li>
                    <li><strong>Direito de oposição:</strong> opor-se ao tratamento dos seus dados</li>
                    <li><strong>Direito de retirar o consentimento:</strong> retirar o consentimento a qualquer momento</li>
                  </ul>
                  <p className="mt-4">
                    Para exercer qualquer destes direitos, contacte-nos através de: <strong>samuelmagalhaes.mtc@gmail.com</strong> ou <strong>961 509 484</strong>
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>11. Cookies</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Este website utiliza cookies essenciais para o seu funcionamento. Os cookies são pequenos ficheiros de texto armazenados no seu dispositivo que nos ajudam a melhorar a sua experiência de navegação.
                  </p>
                  <p>
                    Pode configurar o seu navegador para recusar cookies, mas isso pode afetar algumas funcionalidades do website.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>12. Reclamações</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Se considerar que os seus direitos de proteção de dados foram violados, tem o direito de apresentar uma reclamação à Comissão Nacional de Proteção de Dados (CNPD):
                  </p>
                  <p>
                    <strong>CNPD - Comissão Nacional de Proteção de Dados</strong><br />
                    Av. D. Carlos I, 134, 1º<br />
                    1200-651 Lisboa<br />
                    Tel: +351 213 928 400<br />
                    Email: geral@cnpd.pt<br />
                    Website: www.cnpd.pt
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>13. Alterações a Esta Política</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>
                    Podemos atualizar esta política de privacidade periodicamente. Qualquer alteração será publicada nesta página com a data de atualização revista. Recomendamos que consulte regularmente esta página para se manter informado.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle>14. Contacto</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-stone-700">
                  <p>Para questões sobre esta política de privacidade ou sobre o tratamento dos seus dados pessoais, contacte:</p>
                  <p>
                    <strong>Samuel Magalhães</strong><br />
                    Email: samuelmagalhaes.mtc@gmail.com<br />
                    Telefone: 961 509 484
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm">© 2025 Samuel Magalhães - Terapeuta de Medicina Tradicional Chinesa</p>
          <p className="text-xs text-stone-500 mt-2">Porto | Braga | Portugal</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;